import { Model } from '@vuex-orm/core'
import { Request } from '@vuex-orm/plugin-axios'
import { Condition } from '@vuex-orm/core/lib/modules/payloads/Actions'
import { RelationshipConstraint } from '@vuex-orm/core/lib/query/contracts'
import AppState from '@/utils/app.state'
import Configuration from './Configuration'
import Checkout from './user/Checkout'
import { PivotedExtra } from './Extra'
import Extra from './Extra'
import RatePlanPrice from './RatePlanPrice'
import { MinimalPriceBreakdown } from './common/breakdown'
import { getByRatePlan } from '@/data/service/extras'

/* Function for constructing `where` clause */
const FIND_EXTRA_FN = (checkoutId: number, extraId: number) => (record: CheckoutExtra) => (
  record.extra_id === extraId &&
  record.checkout_id === checkoutId
)

export default class CheckoutExtra extends Model {
  declare answer?: string
  declare checkout_id: number
  declare extra_id: number
  declare quantity: number
  declare includedQuantity: number
  declare totalPrice: number

  static entity = 'checkoutExtra'

  static primaryKey = ['checkout_id', 'extra_id']

  static fields() {
    return {
      answer: this.string(null).nullable(),
      checkout_id: this.attr(null),
      extra_id: this.attr(null),
      quantity: this.number(0),
      includedQuantity: this.number(0),
      totalPrice: this.number(0),
    }
  }

  static addExtrasToCheckout(
    checkoutId: number,
    extras: (CheckoutExtra | number)[] = [],
    quantity = 0,
    included = false
  ) {
    /* Check parameter */
    if (!extras.length) return

    /* Prepare update data */
    const data = extras.map(function (extra) {
      const updExtra = (typeof extra === 'object')
        ? extra
        : { extra_id: extra } as Omit<CheckoutExtra, 'totalPrice'>

      updExtra.checkout_id = checkoutId
      updExtra.quantity = quantity
      if (included) updExtra.includedQuantity = quantity
      return updExtra
    })

    return this.insertOrUpdate({ data })
  }

  static getExtrasForCheckout(checkout: Checkout, modifyQuery?: RelationshipConstraint) {
    if (!checkout || checkout.id == null) return []

    /* Get model with a relation */
    const checkoutWithExtras = Checkout.query()
      .where('id', checkout.id)
      .with('extras', modifyQuery || function () { })
      .first()

    return checkoutWithExtras ? checkoutWithExtras.extras : []
  }

  static getIncludedExtrasForCheckout(checkout: Checkout) {
    return (this.getExtrasForCheckout(checkout) as PivotedExtra[])
      .filter(it => it.pivot.includedQuantity > 0)
  }

  static getSelectedExtrasForCheckout(checkout: Checkout, modifyQuery?: RelationshipConstraint, filterOutIncluded = true) {
    const filterFn = filterOutIncluded
      ? (extra: PivotedExtra) => extra.pivot && (extra.pivot.quantity - extra.pivot.includedQuantity > 0)
      : (extra: PivotedExtra) => extra.pivot && extra.pivot.quantity > 0

    return (this.getExtrasForCheckout(checkout, modifyQuery) as PivotedExtra[])
      .filter(filterFn)
  }

  static getAllSelectedExtras(modifyQuery: RelationshipConstraint) {
    return Checkout.all().flatMap((checkout) => {
      return this.getSelectedExtrasForCheckout(checkout, modifyQuery)
    })
  }

  static deselectAllExtrasFromCheckout(checkout: Checkout) {
    return this.update({
      where: record => record.checkout_id === checkout.id, // todo correct type
      data: { quantity: 0 }
    })
  }

  /**
   * Deselects an extra from the checkout
   * @param {String} checkoutId The id of the associated checkout
   * @param {String} extraId The extra id
   * @param {Number|null} byQuantity By how many should an extra quantity be updated.
   *  `null` indicates to completely deselect an extra
   */
  static deselectExtraFromCheckout(
    checkoutId: number,
    extraId: number,
    byQuantity: number | null = null
  ) {
    return this.update({
      where: FIND_EXTRA_FN(checkoutId, extraId) as Condition,
      data: (record: CheckoutExtra) => {
        /* Update quantity, do not allow going below 0 */
        record.quantity = (byQuantity != null)
          ? Math.max(0, record.quantity - byQuantity)
          : 0
      }
    })
  }

  /**
   * Modifies extra in the checkout
   * @param {Number|String} checkoutId The id of the associated checkout
   * @param {Number|String} extraId The extra id
   * @param {Object} data The update object
   * @returns {Promise<Collection<CheckoutExtra>>}
   */
  static modifyExtraInCheckout(checkoutId: number, extraId: number, data = {}) {
    return this.update({
      where: FIND_EXTRA_FN(checkoutId, extraId) as Condition,
      data
    })
  }

  /**
   * Selects an extra in the checkout
   * @param checkoutId The id of associated checkout
   * @param extraId The extra id
   * @param byQuantity By how many should an extra quantity be updated.
   */
  static selectExtraToCheckout(checkoutId: number, extraId: number, byQuantity = 1, answer?: string) {
    const associatedExtra = Extra.find(extraId)

    return this.update({
      where: FIND_EXTRA_FN(checkoutId, extraId) as Condition,
      data: (record: CheckoutExtra) => {
        /* Update quantity, use max limit */
        const newQuantity = record.quantity + byQuantity
        record.quantity = associatedExtra && associatedExtra.maxQuantityPerRoom > 0
          ? Math.min(newQuantity, associatedExtra.maxQuantityPerRoom)
          : newQuantity
        record.answer = answer
      }
    })
  }

  static async updateWithRatePlanAndCheckout(ratePlan: RatePlanPrice, checkout: Checkout, roomId: string) {
    /* Validate params */
    if (!ratePlan || !ratePlan.ratePlan || !checkout || checkout.id == null) {
      console.warn('Bad arguments provided to updateWithRatePlanAndCheckout:', ratePlan, checkout)
      return false
    }

    /* Remove previous extras */
    CheckoutExtra.delete(record => record.checkout_id === +checkout.id)

    /* Get from the API */
    await this.api().fetchByRatePlan(ratePlan.ratePlan.id, roomId, checkout.id)

    /* Add included extras */
    if (ratePlan.includedExtrasIDs && ratePlan.includedExtrasIDs.length) {
      await CheckoutExtra.addExtrasToCheckout(+checkout.id, ratePlan.includedExtrasIDs, 1, true)
    }

    return true
  }

  static apiConfig = {
    actions: {
      async fetchByRatePlan(this: Request, ratePlanID: string, roomID: string, checkoutId: number) {
        /* Get session */
        const configuration = Configuration.find(1)
        if (!configuration || !configuration.session) { return }

        try {
          /* Make a request */
          const res = await getByRatePlan({
            propertyId: AppState.currentProperty!.toString(),
            session: configuration.session,
            roomConfigurations: { [checkoutId]: { ratePlanID, roomID }}
          })

          /* Get the field (errors will be caught) */
          const extraPrices = res[0].extrasPerRoomConfig

          /* Update min and max quantities on Extra model (IBEV4-564) */
          Extra.updateMinMaxQuantities(extraPrices.map(extra => ({
            extraID: extra.extraID,
            minQuantityPerRoom: extra.minQuantity,
            maxQuantityPerRoom: extra.maxQuantity
          })))

          /* Update model */
          return CheckoutExtra.addExtrasToCheckout(checkoutId, extraPrices.map(extra => ({
            extra_id: extra.extraID,
            totalPrice: extra.totalPrices.reduce((sum: number, price: MinimalPriceBreakdown) => sum + price.totalPrice, 0) as number
          })) as CheckoutExtra[])
        } catch (e) {
          console.warn(e)
          return false
        }
      }
    }
  }
}
